import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Box, Column, ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import { ContactAccountingMapping } from '~/components/ContactForm/ContactAccountingMapping';
import { ContactForm } from '~/components/ContactForm/ContactForm';
import { normalizeContactPhone } from '~/components/ContactForm/ContactForm.utils';
import { useHasLinkedAccounting } from '~/components/ContactForm/hooks/useHasLinkedAccounting';
import { useUpdateClientContact } from '~/hooks/reactQuery/queries/useClientMutations';
import { useApplyAccountingMapping } from '~/hooks/useApplyAccountingMapping';
import { GetContactEmailChangeGuardTID, useContactEmailChangeGuard } from '~/hooks/useContactEmailChangeGuard';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, errorTextsPath } from '~/infra/ErrorResponse';
import { useStoreActions } from '~/store/store-context';
import { CONTACT_FIELD_NAME, CONTACT_FORM_TEST_ID, editContactModalTextsPath, GetEditContactModalTID } from './EditContactModal.utils';
import { useEditContactAccountingPrefill } from './hooks/useEditContactAccountingPrefill';
const UPDATE_CONTACT_SUCCESS_TOAST_TEST_ID = 'edit-contact-success-toast';
function EditContactModalContent(param) {
    let { clientId, contact, dismissModal, lockedClientName, markPending, markComplete, testId } = param;
    const { t } = useLocaleKeys(editContactModalTextsPath);
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const { EditContactModalTestIds } = useMemo(()=>GetEditContactModalTID(testId), [
        testId
    ]);
    const { ContactEmailChangeGuardTestIds } = useMemo(()=>GetContactEmailChangeGuardTID(), []);
    const { updateClientContact } = useUpdateClientContact();
    const { api } = useStoreActions();
    const applyAccountingMapping = useApplyAccountingMapping();
    const { guard } = useContactEmailChangeGuard(contact, {
        biPrefix: 'edit_contact',
        testIds: {
            updateEmailModal: ContactEmailChangeGuardTestIds.UpdateEmailModal(),
            updateEmailConfirmButton: ContactEmailChangeGuardTestIds.UpdateEmailModalConfirmButton(),
            updateEmailCancelButton: ContactEmailChangeGuardTestIds.UpdateEmailModalCancelButton(),
            withdrawModal: ContactEmailChangeGuardTestIds.WithdrawModal(),
            withdrawConfirmButton: ContactEmailChangeGuardTestIds.WithdrawModalConfirmButton(),
            withdrawCancelButton: ContactEmailChangeGuardTestIds.WithdrawModalCancelButton()
        }
    });
    const { showSuccessToast } = useOverlayContext();
    const { handleSubmit } = useFormContext();
    const hasLinkedAccounting = useHasLinkedAccounting();
    const [errorMessage, setErrorMessage] = useState(null);
    const resolvedIntegratedIds = useEditContactAccountingPrefill(contact);
    const saveContact = async (formContact)=>{
        const userId = contact.userId();
        if (userId != null) {
            var _formContact_phone, _contact_phone;
            const updates = [];
            if (formContact.email !== contact.email()) {
                updates.push(api.user.updateBuyerEmail(userId, formContact.email, clientId));
            }
            const nameChanged = formContact.firstName !== contact.firstName() || formContact.lastName !== contact.lastName();
            const phoneChanged = ((_formContact_phone = formContact.phone) !== null && _formContact_phone !== void 0 ? _formContact_phone : '') !== ((_contact_phone = contact.phone()) !== null && _contact_phone !== void 0 ? _contact_phone : '');
            if (nameChanged || phoneChanged) {
                var _normalizeContactPhone;
                updates.push(api.user.updateBuyer(userId, {
                    name: {
                        first: formContact.firstName,
                        last: formContact.lastName
                    },
                    phone: (_normalizeContactPhone = normalizeContactPhone(formContact.phone)) !== null && _normalizeContactPhone !== void 0 ? _normalizeContactPhone : null,
                    customerBusinessId: clientId
                }));
            }
            await Promise.all(updates);
            return contact.version();
        }
        const { version } = await updateClientContact({
            clientId,
            contactId: contact.id(),
            firstName: formContact.firstName,
            lastName: formContact.lastName,
            email: formContact.email,
            phone: normalizeContactPhone(formContact.phone),
            version: contact.version()
        });
        return version;
    };
    const onValid = async (param)=>{
        let { contact: formContact } = param;
        setErrorMessage(null);
        if (await guard(formContact.email) === 'cancel') return;
        const userId = contact.userId();
        const pendingUserId = userId != null && formContact.email !== contact.email() ? userId : null;
        if (pendingUserId != null) markPending === null || markPending === void 0 ? void 0 : markPending(pendingUserId);
        let version;
        try {
            version = await saveContact(formContact);
        } catch (error) {
            if (pendingUserId != null) markComplete === null || markComplete === void 0 ? void 0 : markComplete(pendingUserId);
            setErrorMessage(new ErrorResponse(error).saveContactErrorText(errorTexts));
            return;
        }
        const mappingSuccessful = await applyAccountingMapping(contact.id(), version, {
            qbo: formContact.qbo,
            xero: formContact.xero
        }, resolvedIntegratedIds);
        if (mappingSuccessful) showSuccessToast(t.contactUpdated(), UPDATE_CONTACT_SUCCESS_TOAST_TEST_ID);
        dismissModal();
    };
    const modalProps = {
        title: t.title(),
        primaryButtonLabel: t.saveButton(),
        secondaryButtonLabel: t.cancelButton()
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, modalProps), {
        testId: EditContactModalTestIds.Root(),
        textAlignCenter: false,
        bi: {
            primaryButton: {
                name: 'edit_contact_submit'
            },
            secondaryButton: {
                name: 'edit_contact_cancel'
            },
            closeButton: {
                name: 'edit_contact_close'
            }
        },
        primaryButtonTestId: EditContactModalTestIds.SaveButton(),
        secondaryButtonTestId: EditContactModalTestIds.CancelButton(),
        onPrimaryButtonClick: handleSubmit(onValid),
        onSecondaryButtonClick: dismissModal,
        onCloseButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs(Column, {
            gap: 24,
            children: [
                /*#__PURE__*/ _jsx(ContactForm, {
                    namePrefix: CONTACT_FIELD_NAME,
                    lockedClientName: lockedClientName,
                    accountingMapping: hasLinkedAccounting ? /*#__PURE__*/ _jsx(ContactAccountingMapping, {
                        namePrefix: CONTACT_FIELD_NAME
                    }) : undefined,
                    testId: CONTACT_FORM_TEST_ID
                }),
                errorMessage != null && /*#__PURE__*/ _jsx(Box, {
                    testId: EditContactModalTestIds.UpdateContactError(),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "error",
                        children: errorMessage
                    })
                })
            ]
        })
    }));
}
export function EditContactModal(props) {
    var _props_contact_phone;
    const methods = useForm({
        mode: 'all',
        defaultValues: {
            [CONTACT_FIELD_NAME]: {
                firstName: props.contact.firstName(),
                lastName: props.contact.lastName(),
                email: props.contact.email(),
                phone: (_props_contact_phone = props.contact.phone()) !== null && _props_contact_phone !== void 0 ? _props_contact_phone : '',
                qbo: null,
                xero: null
            }
        }
    });
    return /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, methods), {
        children: /*#__PURE__*/ _jsx(EditContactModalContent, _object_spread({}, props))
    }));
}
